/**
 * 12-arc SVG year donut highlighting the current lunar month.
 */

import React from "react";
import type { YearClimateRow } from "../../../utils/forecast/monthlyConsultation";
import { C } from "../../alignment-advantage/shared/constants";

/** Default diameter for the web monthly consultation viewer. */
export const YEAR_RADIAL_MAP_DEFAULT_SIZE = 220;

/** Larger diameter for the print Year Map (Canva page-14). */
export const YEAR_RADIAL_MAP_PDF_SIZE = 320;

/**
 * Props for the year radial map donut.
 */
export interface YearRadialMapProps {
  /** Twelve lunar-month climate rows. */
  months: YearClimateRow[];
  /** SVG canvas diameter in px. Defaults to {@link YEAR_RADIAL_MAP_DEFAULT_SIZE}. */
  size?: number;
  /** Optional wrapper class (e.g. print hook). */
  className?: string;
}

/**
 * Geometry for one radial map instance, scaled from the 220px base design.
 */
interface RadialLayout {
  size: number;
  cx: number;
  cy: number;
  outerR: number;
  innerR: number;
  gapDeg: number;
  scale: number;
}

/**
 * Clamp and derive layout metrics for a requested canvas size.
 *
 * @param size - Desired SVG diameter in px
 * @returns Scaled layout for arcs, labels, and center copy
 */
function buildRadialLayout(size: number | undefined): RadialLayout {
  const requested =
    typeof size === "number" && Number.isFinite(size) ? Math.round(size) : YEAR_RADIAL_MAP_DEFAULT_SIZE;
  const safeSize = Math.min(480, Math.max(160, requested));
  const scale = safeSize / YEAR_RADIAL_MAP_DEFAULT_SIZE;
  return {
    size: safeSize,
    cx: safeSize / 2,
    cy: safeSize / 2,
    outerR: 92 * scale,
    innerR: 54 * scale,
    gapDeg: 2,
    scale,
  };
}

/**
 * Convert polar degrees (0 = top, clockwise) to cartesian on the SVG canvas.
 *
 * @param layout - Radial geometry for this instance
 * @param radius - Distance from center
 * @param angleDeg - Angle in degrees, 0 at top
 */
const polarToCart = (
  layout: RadialLayout,
  radius: number,
  angleDeg: number
): { x: number; y: number } => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: layout.cx + radius * Math.cos(rad),
    y: layout.cy + radius * Math.sin(rad),
  };
};

/**
 * Build an SVG donut-arc path for one of twelve equal month wedges.
 *
 * @param layout - Radial geometry for this instance
 * @param startDeg - Wedge start angle
 * @param endDeg - Wedge end angle
 */
const arcPath = (layout: RadialLayout, startDeg: number, endDeg: number): string => {
  const outerStart = polarToCart(layout, layout.outerR, startDeg);
  const outerEnd = polarToCart(layout, layout.outerR, endDeg);
  const innerEnd = polarToCart(layout, layout.innerR, endDeg);
  const innerStart = polarToCart(layout, layout.innerR, startDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  const outerR = String(layout.outerR);
  const innerR = String(layout.innerR);
  return [
    `M ${String(outerStart.x)} ${String(outerStart.y)}`,
    `A ${outerR} ${outerR} 0 ${String(largeArc)} 1 ${String(outerEnd.x)} ${String(outerEnd.y)}`,
    `L ${String(innerEnd.x)} ${String(innerEnd.y)}`,
    `A ${innerR} ${innerR} 0 ${String(largeArc)} 0 ${String(innerStart.x)} ${String(innerStart.y)}`,
    "Z",
  ].join(" ");
};

/**
 * Resolve fill / opacity for a month arc based on current / past / future.
 *
 * @param row - Month climate row
 * @param currentMonth - Active lunar month number
 */
const arcStyle = (
  row: YearClimateRow,
  currentMonth: number
): { fill: string; opacity: number } => {
  if (row.isCurrent) {
    return { fill: C.coral, opacity: 1 };
  }
  if (row.lunarMonth < currentMonth) {
    return { fill: C.navy, opacity: 0.28 };
  }
  return { fill: C.gold, opacity: 0.55 };
};

/**
 * SVG year ring with 12 lunar-month arcs and a small legend.
 */
export const YearRadialMap: React.FC<YearRadialMapProps> = ({
  months,
  size,
  className,
}) => {
  const layout = buildRadialLayout(size);
  const sorted = [...months].sort((a, b) => a.lunarMonth - b.lunarMonth);
  const current = sorted.find((m) => m.isCurrent);
  const currentMonth = current !== undefined ? current.lunarMonth : 1;
  const slice = 360 / 12;
  const wrapperClass =
    typeof className === "string" && className.trim().length > 0
      ? className.trim()
      : undefined;

  const labelCurrentSize = 11 * layout.scale;
  const labelSize = 9 * layout.scale;
  const centerTitleSize = 11 * layout.scale;
  const centerSubtitleSize = 10 * layout.scale;
  const currentStroke = Math.max(1.5, 2 * layout.scale);
  const innerHoleInset = 6 * layout.scale;
  const centerTitleOffset = 6 * layout.scale;
  const centerSubtitleOffset = 10 * layout.scale;
  const legendFontSize = 10 * layout.scale;
  const legendSwatch = 10 * layout.scale;

  return (
    <div className={wrapperClass ?? "flex flex-col items-center gap-3"}>
      <svg
        width={layout.size}
        height={layout.size}
        viewBox={`0 0 ${String(layout.size)} ${String(layout.size)}`}
        aria-label="Year climate map"
        role="img"
      >
        {sorted.slice(0, 12).map((row) => {
          const index = row.lunarMonth - 1;
          const start = index * slice + layout.gapDeg / 2;
          const end = (index + 1) * slice - layout.gapDeg / 2;
          const style = arcStyle(row, currentMonth);
          const mid = (start + end) / 2;
          const labelPos = polarToCart(
            layout,
            (layout.outerR + layout.innerR) / 2,
            mid
          );
          return (
            <g key={row.lunarMonth}>
              <path
                d={arcPath(layout, start, end)}
                fill={style.fill}
                opacity={style.opacity}
                stroke={row.isCurrent ? C.coralDark : "transparent"}
                strokeWidth={row.isCurrent ? currentStroke : 0}
              />
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={row.isCurrent ? labelCurrentSize : labelSize}
                fontWeight={row.isCurrent ? 700 : 600}
                fill={row.isCurrent ? "#fff" : C.navy}
              >
                {`M${String(row.lunarMonth)}`}
              </text>
            </g>
          );
        })}
        <circle
          cx={layout.cx}
          cy={layout.cy}
          r={layout.innerR - innerHoleInset}
          fill={C.cream}
        />
        <text
          x={layout.cx}
          y={layout.cy - centerTitleOffset}
          textAnchor="middle"
          fontSize={centerTitleSize}
          fontWeight={700}
          fill={C.navy}
        >
          Year
        </text>
        <text
          x={layout.cx}
          y={layout.cy + centerSubtitleOffset}
          textAnchor="middle"
          fontSize={centerSubtitleSize}
          fill={C.muted}
        >
          {`M${String(currentMonth)} now`}
        </text>
      </svg>
      <div
        className="flex flex-wrap items-center justify-center gap-3 font-semibold"
        style={{ fontSize: legendFontSize }}
      >
        <span className="inline-flex items-center gap-1.5" style={{ color: C.navy }}>
          <span
            className="rounded-sm"
            style={{
              width: legendSwatch,
              height: legendSwatch,
              background: C.navy,
              opacity: 0.28,
            }}
          />
          Past
        </span>
        <span className="inline-flex items-center gap-1.5" style={{ color: C.navy }}>
          <span
            className="rounded-sm"
            style={{
              width: legendSwatch,
              height: legendSwatch,
              background: C.coral,
            }}
          />
          Current
        </span>
        <span className="inline-flex items-center gap-1.5" style={{ color: C.navy }}>
          <span
            className="rounded-sm"
            style={{
              width: legendSwatch,
              height: legendSwatch,
              background: C.gold,
              opacity: 0.55,
            }}
          />
          Ahead
        </span>
      </div>
    </div>
  );
};
